import CircleUserSvg from "@icons/circle-user.svg?react";
import CitySvg from "@icons/city.svg?react";
import CrossSvg from "@icons/cross.svg?react";
import MenuBurgerSvg from "@icons/menu-burger.svg?react";
import { t } from "@lingui/core/macro";
import React from "react";
import { Link, NavLink, useLocation } from "react-router-dom";

import { useLazyScrollbarToggle } from "@/common/hooks/use-scrollbar-toggle";
import { useToggle } from "@/common/hooks/use-toggle";
import { useCompany } from "@/common/providers/company-provider";
import { useFeatureProvider } from "@/common/providers/feature-provider";
import {
  Screen,
  useSidebar,
} from "@/common/providers/sidebar-provider/sidebar-provider";

import { CompanyMenu } from "../company/company-menu/company-menu";
import { Logo } from "../logo/logo";
import { NavBar } from "../nav-bar/nav-bar";
import { getClassName } from "../nav-bar/nav-bar.utils";
import { NotificationBell } from "../push-notifications/push-notifications";
import { UserMenu } from "../user/user-menu/user-menu";

const TopBar = () => {
  const toggleBodyScrollbar = useLazyScrollbarToggle();
  const sidebar = useSidebar();
  const { user, company } = useCompany();
  const expanded = useToggle();
  const expandedProfile = useToggle();
  const expandedCompany = useToggle();
  const { pathname } = useLocation();
  const close = expanded.setOff;
  const features = useFeatureProvider();

  React.useEffect(() => {
    close();
  }, [pathname, close]);

  const handleToggle = () => {
    expanded.toggle();
    toggleBodyScrollbar(!expanded.on);
  };

  const handleToggleProfile = () => {
    expandedProfile.toggle();
  };

  const handleMenuAction = (screen: Screen) => () => {
    expandedProfile.toggle();
    expanded.toggle();
    sidebar.actions.openScreen(screen);
  };

  return (
    <div className="can-top-bar fixed z-1 flex w-full justify-between bg-white sm:hidden">
      {expanded.on ? null : <Logo className="ml-6 md:hidden" />}
      <div
        className={`flex basis-full flex-wrap items-center justify-between gap-6 ${expanded.on ? "mt-5" : ""}`}
      >
        {expanded.on ? (
          <>
            <UserButton onClick={handleToggleProfile}>{user.name}</UserButton>
            <CloseButton onClick={handleToggle} />

            {expandedProfile.on ? (
              <nav className="text-can-forest-teal z-10 basis-full bg-white px-6 font-bold">
                <UserMenu.Mobile>
                  <UserMenu
                    renderItem={{
                      plans: (item) => (
                        <Link className="flex w-full gap-4" to="/pricing">
                          {item.icon}
                          {item.label}
                        </Link>
                      ),
                      profile: (item) => (
                        <button
                          className="flex w-full gap-4"
                          onClick={handleMenuAction("user-profile")}
                        >
                          {item.icon}
                          {item.label}
                        </button>
                      ),
                      subscription: (item) => (
                        <button
                          className="flex w-full gap-4"
                          onClick={handleMenuAction("subscriptions")}
                        >
                          {item.icon}
                          {item.label}
                        </button>
                      ),
                    }}
                  />
                </UserMenu.Mobile>
              </nav>
            ) : null}
            <div className="flex w-full flex-col">
              <CompanyButton onClick={expandedCompany.toggle}>
                {company?.name}
              </CompanyButton>
              {expandedCompany.on ? (
                <CompanyMenu.Mobile>
                  <CompanyMenu />
                </CompanyMenu.Mobile>
              ) : null}
            </div>
          </>
        ) : (
          <div className="mt-4 ml-auto">
            <NotificationBell />
            <button
              className="ml-6"
              data-testid="mobile-menu-toggle"
              onClick={handleToggle}
            >
              <MenuBurgerSvg className="mr-6 h-6 w-6" />
            </button>
          </div>
        )}
        {expanded.on ? (
          <aside className="bg-can-forest-teal z-1 flex h-[100dvh] w-full flex-col pt-6">
            <NavBar
              plan={{
                features,
                planId:
                  company?.plan.id ??
                  Object.values(features)[0]?.planId ??
                  null,
                type: company?.plan.type ?? "BASIC",
              }}
              renderItem={(link, Wrapper) => (
                <NavLink
                  className={({ isActive }) =>
                    `justify-start! gap-4 px-6 ${getClassName({ isActive })}`
                  }
                  key={link.to}
                  to={link.to}
                  viewTransition
                >
                  <Wrapper>{link.icon}</Wrapper>
                  {link.label}
                </NavLink>
              )}
            />
          </aside>
        ) : null}
      </div>
    </div>
  );
};

const UserButton = ({
  children,
  onClick,
}: React.PropsWithChildren<{ onClick: () => void }>) => {
  return (
    <button
      className="text-can-forest-teal ml-6 flex gap-2 font-bold"
      data-testid="user-profile"
      onClick={onClick}
    >
      <CircleUserSvg /> {children}
    </button>
  );
};

const CloseButton = ({ onClick }: { onClick: () => void }) => {
  return (
    <button
      aria-label={t`Zavřít`}
      className="mr-6"
      data-testid="mobile-menu-toggle"
      onClick={onClick}
    >
      <CrossSvg />
    </button>
  );
};

const CompanyButton = ({
  children,
  onClick,
}: React.PropsWithChildren<{ onClick: () => void }>) => {
  return (
    <button
      className="text-can-forest-teal ml-6 flex gap-2 font-bold"
      data-testid="company-menu-button"
      onClick={onClick}
    >
      <CitySvg /> {children}
    </button>
  );
};

export { TopBar };
